import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { GeographySpend } from '@/hooks/useDiversityAnalytics';

interface DiversityByGeographyChartProps {
  data: GeographySpend[] | undefined;
  isLoading: boolean;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatCurrencyShort(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}k`;
  }
  return `$${value.toFixed(0)}`;
}

function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

// Color gradient for geographic distribution
const getBarColor = (index: number, total: number) => {
  const hue = 210; // Blue base
  const saturation = 70;
  const lightness = 65 - (index / total) * 25; // Darker for top states
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export function DiversityByGeographyChart({ data, isLoading }: DiversityByGeographyChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[350px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Diversity Spend by Geography</CardTitle>
          <CardDescription>
            Geographic distribution of diversity spend by vendor location
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            No geographic data available for the selected period
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data
  const chartData = data.map((item, index) => ({
    state: item.state,
    spend: item.diverseSpendCents / 100,
    spendCents: item.diverseSpendCents,
    vendors: item.diverseVendorCount,
    jobs: item.jobCount,
    percentage: item.percentageOfDiverseSpend,
    fill: getBarColor(index, data.length),
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg border bg-background p-3 shadow-lg">
          <p className="font-medium">{data.state}</p>
          <div className="mt-2 space-y-1 text-sm">
            <p>Diversity Spend: <span className="font-medium">{formatCurrency(data.spendCents)}</span></p>
            <p>Diverse Vendors: <span className="font-medium">{data.vendors}</span></p>
            <p>Jobs: <span className="font-medium">{data.jobs}</span></p>
            <p>% of Diversity Spend: <span className="font-medium">{formatPercentage(data.percentage)}</span></p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Diversity Spend by Geography</CardTitle>
        <CardDescription>
          Geographic distribution of diversity spend by vendor location
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="h-[350px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis
                type="number"
                tickFormatter={formatCurrencyShort}
              />
              <YAxis
                type="category"
                dataKey="state"
                width={80}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="spend" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>State/Region</TableHead>
                <TableHead className="text-right">Diversity Spend</TableHead>
                <TableHead className="text-right">% of Diversity</TableHead>
                <TableHead className="text-right">Vendors</TableHead>
                <TableHead className="text-right">Jobs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((item) => (
                <TableRow key={item.state}>
                  <TableCell className="font-medium">{item.state}</TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(item.diverseSpendCents)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatPercentage(item.percentageOfDiverseSpend)}
                  </TableCell>
                  <TableCell className="text-right">{item.diverseVendorCount}</TableCell>
                  <TableCell className="text-right">{item.jobCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
